import _ from 'lodash'
import { Box, Button, Card, CardActionArea, Modal, Typography } from '@mui/material'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import ApiClient from '../../backend/ApiClient'
import { useAppDispatch } from '../../store/Hooks'
import { changeRoute } from '../../store/slices/SessionSlice'
import RouteHeader from '../controls/RouteHeader'
import SmartCard from '../controls/cards/SmartCard'
import StaleObserver from '../controls/stale/StaleObserver'
import SmartTable from '../controls/table/SmartTable'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'
import { IUploadedFile, IUploadedResult } from '../../definitions/Upload'
import TimeHelper from '../../helpers/TimeHelper'
import Formatter from '../../helpers/Formatter'
import CustomIcon from '../icons/CustomIcon'
import { SmartTableUtilities } from '../controls/table'
import { useState } from 'react'
import { UploadRounded } from '@mui/icons-material'
import UploadModal from '../new_contract_editor/UploadModal'

export default function ContractManagerPage() {
    const dispatch = useAppDispatch()

    const { status: filesStatus, response: files } = useQuery({
        queryKey: buildQueryKey('files'),
        queryFn: async () => await ApiClient.getAllFiles(),
        cache: true
    })

    const lastRecent = _.orderBy((files ?? []), x => x.uploadedAt, 'desc')

    const renderRecentCard = (file: IUploadedFile) => {
        const contractId = file.contractId
        const allContractFile = (files ?? []).filter(x => x?.contractId === contractId)

        return (
            <Card key={file.uuid} sx={{ minWidth: 'calc(var(--unit)*30)', maxWidth: 'calc(var(--unit)*30)' }}>
                <CardActionArea sx={{ padding: 1 }} onClick={() => dispatch(changeRoute({ route: 'contract-editor', params: { contractId: file.contractId, uploadedFiles: allContractFile } }))}>
                    <Box flexDirection='row' display='flex' gap={1} alignItems='center'>
                        <Box minWidth='calc(var(--unit)*5)' height='calc(var(--unit)*5)'>
                            <CustomIcon name='pdf' />
                        </Box>
                        <Box overflow='hidden'>
                            <Typography variant='body2' fontWeight='bold' fontSize={12} whiteSpace='nowrap' textOverflow='ellipsis'>{file.name}</Typography>
                            <Typography variant='caption'>
                                {Formatter.date(new Date(file?.uploadedAt ?? ''))}
                                {' · '}
                                {Formatter.bytes(file?.size, 2)}
                            </Typography>
                        </Box>
                    </Box>
                </CardActionArea>
            </Card>
        )
    }

    return (
        <Tile.Container>
            <RouteHeader
                primary='Contracts Manager'
                actions={<UploadContractButton />}
            />
            <Tile.Column spacing={2}>
                <Tile.Column flex={0}>
                    <Typography variant='h6'>Recents</Typography>
                    {lastRecent.length > 0
                        ? (
                                <Tile.Row spacing={2} style={{ overflowX: 'auto', width: '100%', padding: 'var(--unit) 0' }}>
                                    {lastRecent?.map(x => renderRecentCard(x))}
                                </Tile.Row>
                            )
                        : (
                                <Box width='100%' height='100%' padding={3} display='flex' justifyContent='center' alignItems='center' flexDirection='column'>
                                    <InsertDriveFileIcon fontSize='large' />
                                    <Typography marginTop={1}>No Recents Found</Typography>
                                </Box>
                            )}
                </Tile.Column>
                <Tile.Tile flex={1}>
                    <SmartCard flex={1}>
                        <SmartTable
                            columns={[
                                { id: 'name', label: 'Name' },
                                { id: 'uploadedAt', label: 'Uploaded At' },
                                { id: 'uploadedBy', label: 'Uploaded By' }
                            ]}
                            noCollapse
                            onClickRow={i => dispatch(changeRoute({ route: 'contract-editor', params: { contractId: files[i].contractId, uploadedFiles: (files ?? []).filter(x => x?.contractId === files[i].contractId) } }))}
                            isLoading={filesStatus === 'loading'}
                            datasource={{
                                rows: (files ?? [])?.map((x, i) => SmartTableUtilities.toRow([
                                    { value: x.name },
                                    { value: TimeHelper.formatDateToYYYYMMDD(x.uploadedAt) },
                                    { value: x.uploadedBy }
                                ], i))
                            }}
                        />
                    </SmartCard>
                </Tile.Tile>
            </Tile.Column>
            <StaleObserver status={filesStatus} />
        </Tile.Container>
    )
}

const UploadContractButton = () => {
    const dispatch = useAppDispatch()
    const [open, setOpen] = useState(false)

    const onFinish = (res: IUploadedResult) => {
        dispatch(changeRoute({ route: 'contract-editor', params: { contractId: res.contractId, uploadedFiles: res.uploadedfiles } }))
    }

    return (
        <>
            <Button
                variant='contained'
                startIcon={(
                    <UploadRounded />
                )}
                onClick={() => setOpen(true)}>
                <Typography variant='button'>Upload Contract</Typography>
            </Button>
            <Modal open={open} onClose={() => setOpen(false)}>
                <div>
                    <UploadModal onFinish={onFinish} />
                </div>
            </Modal>
        </>
    )
}
